import { ChangeDetectorRef, Directive, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ContentCard } from 'src/app/models/content-card';
import { Employer } from 'src/app/models/employer';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslateDirective } from 'src/app/directives/translate.directive';

/**
 * Base component for pages that support translation
 * Provides common translation functionality for collecting, requesting, and applying translations
 */
@Directive()
export abstract class TranslatablePageBaseComponent implements OnInit, OnDestroy {
	@ViewChildren(TranslateDirective) translateDirectives: QueryList<TranslateDirective>;

	public employer: Employer;
	public path: string;
	public content: any = {};
	protected contentTypes: { [categoryId: string]: { [key: string]: number } } = {};
	// Catalog defaults (defaultTextContent) keyed like `content`. `content` holds
	// only employer overrides; fields that should fall back to the DB default
	// read from here explicitly (opt-in — nothing existing changes behavior).
	public contentDefaults: { [categoryId: string]: { [key: string]: string } } = {};

	/**
	 * Hero Focal Point presets -> `background-position`.
	 *
	 * The hero crops with `cover`, and the container swings from ~0.85:1 on a
	 * phone to ~1.86:1 on a wide desktop, so a fixed crop point cuts subjects
	 * off. These nine cells let an employer say which part of their own photo
	 * has to stay in frame. Labels come from the dropdown defined in
	 * db/migrations/2026-08-24_hero-focal-point.sql and must match it exactly.
	 *
	 * 25/50/75 rather than 0/50/100: the extreme values pin the very edge of the
	 * image to the edge of the frame, which crops harder than intended when the
	 * subject is merely "towards the top" rather than flush against it.
	 */
	private static readonly FOCAL_POSITIONS: { [label: string]: string } = {
		'Center': '50% 50%',
		'Upper left': '25% 25%',
		'Upper middle': '50% 25%',
		'Upper right': '75% 25%',
		'Left': '25% 50%',
		'Right': '75% 50%',
		'Lower left': '25% 75%',
		'Lower middle': '50% 75%',
		'Lower right': '75% 75%',
	};

	/** Returns '' for empty/unknown labels so the caller leaves CSS in charge. */
	protected focalPointToPosition(label: string | undefined): string {
		return TranslatablePageBaseComponent.FOCAL_POSITIONS[(label || '').trim()] || '';
	}

	protected destroy$ = new Subject<void>();
	protected translationsLoaded = false;

	constructor(
		protected dataService: DataService,
		protected siteService: SiteService,
		protected changeDetector: ChangeDetectorRef,
		protected translationService: TranslationService
	) {
		// Listen for language changes
		this.translationService.currentLanguage$.pipe(
			takeUntil(this.destroy$)
		).subscribe(lang => {
			// Switch video tracks immediately when language changes
			this.switchVideoTrack(lang);
			
			if (this.employer) {
				// Always reload employer data to get fresh English content
				// Then translate if needed (translation API will handle caching)
				this.dataService.getEmployer(this.employer.employerID).subscribe(e => {
					this.loadEmployerData(e);
				});
			}
		});
	}

	ngOnInit(): void {
		// Child components should call setupLanguageChangeSubscription() in their ngOnInit
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Load employer data and update the view
	 */
	protected loadEmployerData(e: Employer): void {
		this.employer = e;
		this.siteService.setEmployer(this.employer);
		
		// Process content categories
		this.employer.employerContent.contentCategories.forEach((category) => {
			this.content[category.contentCategoryID] = category.content.reduce(
				(acc, contentItem) => ({
					...acc,
					[contentItem.contentDesc]: contentItem.textContent ||  contentItem.fileContent
				}),
				{}
			);
			this.contentTypes[category.contentCategoryID] = category.content.reduce(
				(acc, contentItem) => ({
					...acc,
					[contentItem.contentDesc]: contentItem.contentTypeID
				}),
				{}
			);
			this.contentDefaults[category.contentCategoryID] = category.content.reduce(
				(acc, contentItem) => ({
					...acc,
					[contentItem.contentDesc]: contentItem.defaultTextContent
				}),
				{}
			);
		});

		// Load cards (implementation varies by page)
		this.loadCards();

		// Update page-specific content
		this.updatePageContent();

		this.changeDetector.detectChanges();
		this.siteService.pageInitDone();

		// Trigger translation after content is loaded
		setTimeout(() => this.translatePage(), 100);
	}

	/**
	 * Translate all content on the page
	 */
	protected translatePage(): void {
		if (!this.employer || !this.employer.employerID) {
			return;
		}

		// Collect all translatable items
		const items: Array<{ id: string; text: string }> = [];

		// Collect from translate directives (static content)
		if (this.translateDirectives) {
			this.translateDirectives.forEach(directive => {
				const id = directive.getId();
				const text = directive.getOriginalText();
				if (id && text) {
					items.push({ id, text });
				}
			});
		}

		// Collect dynamic content (cards, content object, etc.)
		this.collectDynamicContent(items);

		// If no items to translate, return
		if (items.length === 0) {
			return;
		}

		// Call translation service
		this.translationService.translatePage(
			this.employer.employerID,
			this.getPagePath(),
			items
		).subscribe(translations => {
			// Apply translations to directives
			if (this.translateDirectives) {
				this.translateDirectives.forEach(directive => {
					const id = directive.getId();
					if (translations[id]) {
						directive.applyTranslation(translations[id]);
					}
				});
			}

			// Apply translations to dynamic content
			this.applyDynamicTranslations(translations);
			this.translationsLoaded = true;
			this.changeDetector.detectChanges();
		});
	}

	/**
	 * Collect dynamic content for translation
	 */
	protected collectDynamicContent(items: Array<{ id: string; text: string }>): void {
		// Content types that should never be translated (URLs, images, booleans, dropdowns)
		const nonTranslatableTypes = [2, 5, 6, 9];

		// Text fields (type 1) that hold non-translatable data (phone numbers, codes, email)
		const excludeKeys = ['Group Code', 'Toll Free Number', 'Phone Number', 'Email'];

		// Collect card content
		const cards = this.getCards();
		const cardPrefix = this.getCardPrefix();

		if (cards && cards.length > 0) {
			cards.forEach((card, index) => {
				if (card.title) {
					items.push({ id: `${cardPrefix}-title-${index}`, text: card.title });
				}
				if (card.cardContent) {
					items.push({ id: `${cardPrefix}-content-${index}`, text: card.cardContent });
				}
				if (card.tag) {
					items.push({ id: `${cardPrefix}-tag-${index}`, text: card.tag });
				}
				// Topic cards have cardContentDetail
				if (card.cardContentDetail) {
					items.push({ id: `${cardPrefix}-detail-${index}`, text: card.cardContentDetail });
				}
			});
		}

		// Collect dynamic content from API
		Object.keys(this.content).forEach(categoryId => {
			const category = this.content[categoryId];
			const types = this.contentTypes[categoryId] || {};
			Object.keys(category).forEach(key => {
				const value = category[key];
				const typeID = types[key];
				// Skip non-translatable content types, excluded text keys, and non-string values
				if (typeID && nonTranslatableTypes.includes(typeID)) return;
				if (excludeKeys.includes(key)) return;
				if (value && typeof value === 'string' && value.trim()) {
					items.push({ id: `content-${categoryId}-${key}`, text: value });
				}
			});
		});
	}

	/**
	 * Apply translations to dynamic content
	 */
	protected applyDynamicTranslations(translations: { [key: string]: string }): void {
		// Translate cards
		const cards = this.getCards();
		const cardPrefix = this.getCardPrefix();
		
		if (cards && cards.length > 0) {
			cards.forEach((card, index) => {
				const titleKey = `${cardPrefix}-title-${index}`;
				const contentKey = `${cardPrefix}-content-${index}`;
				const tagKey = `${cardPrefix}-tag-${index}`;
				const detailKey = `${cardPrefix}-detail-${index}`;

				if (translations[titleKey]) {
					card.title = translations[titleKey];
				}
				if (translations[contentKey]) {
					card.cardContent = translations[contentKey];
				}
				if (translations[tagKey]) {
					card.tag = translations[tagKey];
				}
				if (translations[detailKey]) {
					card.cardContentDetail = translations[detailKey];
				}
			});
		}

		// Translate dynamic content object
		Object.keys(this.content).forEach(categoryId => {
			const category = this.content[categoryId];
			Object.keys(category).forEach(key => {
				const translationKey = `content-${categoryId}-${key}`;
				if (translations[translationKey]) {
					this.content[categoryId][key] = translations[translationKey];
				}
			});
		});

		// Apply card-specific translations (e.g., update topicCardsToShow)
		this.applyCardSpecificTranslations();
	}

	// Abstract methods that child components must implement

	/**
	 * Get the page path for translation API calls
	 * @example '/home' or '/learn-more'
	 */
	protected abstract getPagePath(): string;

	/**
	 * Get the cards array for this page
	 * @example videoCards or topicCards
	 */
	protected abstract getCards(): Array<ContentCard>;

	/**
	 * Get the prefix used for card translation IDs
	 * @example 'video-card' or 'topic-card'
	 */
	protected abstract getCardPrefix(): string;

	/**
	 * Load cards from employer data
	 */
	protected abstract loadCards(): void;

	/**
	 * Update page-specific content (hero images, headings, etc.)
	 */
	protected abstract updatePageContent(): void;

	/**
	 * Called when language changes (non-English)
	 * Update any dynamic text that depends on language
	 */
	protected abstract onLanguageChange(): void;

	/**
	 * Apply any card-specific translation logic
	 * @example Refresh topicCardsToShow for learn-more page
	 */
	protected abstract applyCardSpecificTranslations(): void;

	/**
	 * Switch video caption tracks based on the current language
	 * Automatically called when language changes
	 */
	protected switchVideoTrack(language: string): void {
		// Find all video elements in the component
		const videos = document.querySelectorAll('video');
		videos.forEach((video: HTMLVideoElement) => {
			const tracks = video.textTracks;
			if (tracks && tracks.length > 0) {
				// Disable all tracks first
				for (let i = 0; i < tracks.length; i++) {
					tracks[i].mode = 'disabled';
				}
				// Enable the track for the current language (if not English)
				if (language !== 'en') {
					for (let i = 0; i < tracks.length; i++) {
						if (tracks[i].language === language) {
							tracks[i].mode = 'showing';
							break;
						}
					}
				}
			}
		});
	}
}
